import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EditorHost, PageRef } from '@nexus/editor/react';
import type { PageRecord } from '@nexus/store';
import { useStore } from '../db/context';
import { usePageJSON } from '../db/content';
import { openAttachment, uploadAttachment, useAttachment } from '../db/attachments';
import { useAiWrite } from '../ai/writeState';
import { findByName, searchCatalog, useCatalog } from '../state/catalog';
import { useUi } from '../state/ui';
import { TaskQueryBlock } from '../components/tasks/TaskQueryBlock';
import { PageIcon } from '../components/PageIcon';
import { openUseTemplate } from '../templates/state';
import { promoteTask } from '../components/tasks/promote';

const ref = (p: PageRecord): PageRef => ({ id: p.id, title: p.title, icon: p.icon });

/** Adapter from the app (store, catalogue, tabs, i18n) to what the editor needs. */
export function useEditorHost(): EditorHost {
  const store = useStore();
  const { t } = useTranslation();
  return useMemo<EditorHost>(
    () => ({
      searchPages: (query, limit) => searchCatalog(query, limit).map(ref),
      usePage: (id) => {
        const page = useCatalog((s) => (id ? s.byId.get(id) : undefined));
        return useMemo(() => (page ? ref(page) : null), [page]);
      },
      findPageByName: (name) => {
        const p = findByName(name);
        return p ? ref(p) : null;
      },
      openPage: (id, newTab) => useUi.getState().open({ kind: 'page', pageId: id }, newTab),
      createPage: async (title) => ref(await store.createPage({ title })),
      openTag: (tag) => useUi.getState().open({ kind: 'tag', tag }),
      listTags: (query) => {
        const q = query.toLowerCase();
        return useCatalog.getState().tags.filter((tag) => tag.includes(q));
      },
      useEmbedContent: (pageId) => usePageJSON(pageId),
      useAttachment: (target) => useAttachment(target),
      openAttachment: (target) => void openAttachment(store, target),
      uploadFile: async (file) => {
        const name = await uploadAttachment(store, file);
        if (!name) window.alert(t('editor.fileTooLarge', { name: file.name, max: 25 }));
        return name;
      },
      t: (key, vars) => t(key, vars),
      aiWrite: (editor) => useAiWrite.getState().open(editor),
      TaskQuery: TaskQueryBlock,
      PageIcon,
      promoteTask: (taskId) => {
        const { tabs, active } = useUi.getState();
        const tab = tabs[active];
        if (tab?.kind === 'page') void promoteTask(store, { id: taskId, pageId: tab.pageId });
      },
      insertTemplate: (editor) => openUseTemplate({ templateId: null, mode: 'insert', editor }),
    }),
    [store, t],
  );
}
